import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation, useNavigate } from "react-router-dom";

import { useTheme } from "@mui/material/styles";

import { useDialogs, useSnackbar } from "@/context";
import { ActionState, ActionType } from "@/types";


export const useServices = () => {
  const theme = useTheme();
  const { user, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();

  const { openDialog, closeDialog } = useDialogs();
  const { openSnackbar } = useSnackbar();

  const auth = useMemo(
    () => ({ getAccessTokenSilently, user}),
    [getAccessTokenSilently, user]
  );

  // Location is kept out of nav so callbacks depending on nav aren't recreated on every navigation
  const nav = useMemo(
    () => ({ navigate }),
    [navigate]
  );

  const ui = useMemo(
    () => ({ openSnackbar, openDialog, closeDialog, theme }),
    [openSnackbar, openDialog, closeDialog, theme]
  );

  return useMemo(() => ({ auth, nav, ui, location }), [auth, nav, ui, location]);
}

/** Access token getter for API calls, e.g. inside a query function */
export const useToken = () => useAuth0().getAccessTokenSilently;

/** Returns `value` once it has stopped changing for `delay` ms */
export const useDebouncedValue = <T,>(value: T, delay = 300) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timeout);
  }, [value, delay]);

  return debounced;
}

export const useActionWorkflow = () => {

  const [action, setAction] = useState<ActionState>({
    status: 'idle',
    type: null,
    error: null,
    message: ''
  });

  const updateMessage = useCallback((message: string) => {
    setAction(prev => ({ ...prev, message }));
  }, []);

  // Runs can overlap (e.g. site catchments), so the most recently started run
  // owns the action state and earlier runs finish without changing it
  const latestRun = useRef(0);

  const runAction = useCallback(async (type: ActionType, message: string, task: (taskMessage: (message: string) => void) => Promise<any>) => {
    const run = ++latestRun.current;
    const isLatest = () => run === latestRun.current;

    setAction({ status: 'loading', type, error: null, message });
    try {
      await task((message) => isLatest() && updateMessage(message));
      if (isLatest()) setAction({ status: 'success', type, error: null, message: '' });
    } catch (err: any) {
      if (isLatest()) setAction({ status: 'error', type, error: err.message || null, message: '' })
    }
  }, [updateMessage]);

  // Clears the result of the last action. A running action is left alone so
  // its loading state and result aren't lost.
  const resetAction = useCallback(() => {
    setAction(prev => prev.status === 'loading'
      ? prev
      : { status: 'idle', type: null, error: null, message: '' }
    );
  }, []);

  return useMemo(
    () => ({ action, runAction, resetAction, updateMessage }),
    [action, runAction, resetAction, updateMessage]
  )
}
